/**
 * The per-navigation effects every page tree mounts once (moved from App.tsx
 * for the entity route modules' shell, web plan W4.4): `<App/>` for the pages
 * still on the catch-all, src/routes/shell.tsx for the route modules.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageViews } from '@/hooks/usePageViews';
import { rememberLocale } from '@/lib/rememberedLocale';
import { splitLocalePath } from '@/seo/urls';

/**
 * Reset scroll position on every route change (overrides CSS scroll-smooth).
 *
 * With a `#hash` we scroll to that element instead, because the browser's own
 * anchor jump never happens here: on a client-side navigation there is no
 * document load to trigger it, and on a cold load of a deep link the target is
 * still inside an unresolved lazy chunk. Hence the bounded rAF retry — it waits
 * for the page chunk to paint, then gives up and leaves the view at the top
 * rather than scrolling at some arbitrary later moment.
 */
export function ScrollToTop(): null {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    let frame = 0;
    // ~1s at 60fps: long enough for a lazy chunk on a slow connection, short
    // enough that a hash pointing at nothing settles quickly.
    let attempts = 60;
    let lastOffset = NaN;
    let stableFrames = 0;
    const seek = () => {
      const el = document.getElementById(id);
      if (el) {
        // Document offset, not viewport position, so it is unchanged by our own
        // scrolling and only moves when something above the target reflows.
        const offset = Math.round(el.getBoundingClientRect().top + window.scrollY);
        if (offset !== lastOffset) {
          // Re-align rather than stop at the first hit: a web font swapping in
          // or an image getting its height still pushes the heading around
          // after the chunk paints, which would otherwise leave the reader a
          // screen or two short of the section they asked for.
          lastOffset = offset;
          stableFrames = 0;
          el.scrollIntoView({ behavior: 'instant', block: 'start' });
        } else if (++stableFrames >= 3) {
          return;
        }
      }
      if (attempts-- > 0) frame = requestAnimationFrame(seek);
    };
    seek();
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);
  return null;
}

/**
 * Report a GA4 page_view on every client-side navigation (prod only); on
 * /search, once per settled query string rather than per keystroke rewrite.
 */
export function PageViews(): null {
  usePageViews();
  return null;
}

/**
 * Remembers the country of the prefixed page being shown (web plan MW6), so
 * the global account pages (`/favorites`, …) keep it. Client-only, after paint.
 */
export function RememberLocale(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    const locale = splitLocalePath(pathname)?.locale;
    if (locale) rememberLocale(locale);
  }, [pathname]);
  return null;
}
